// src/components/PianoViewport.js
import React, { useLayoutEffect, useRef, useState, useCallback, useEffect } from "react";

/**
 * PianoViewport (hybrid)
 * - <420px (narrow portrait phones): no scale, horizontal scroll (robust on Android)
 * - 420px–1023px: scale-to-fit (no scroll)
 * - >=1024px: natural size (no scale)
 */
export default function PianoViewport({
  children,
  naturalWidth = 1024,
  naturalHeight = 360,
  desktopBreakpoint = 1024,
  narrowPortrait = 420,
  cardClassName = "",
}) {
  const cardRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [wrapH, setWrapH] = useState(naturalHeight);
  const [scrollMode, setScrollMode] = useState(false);

  const recalc = useCallback(() => {
    const card = cardRef.current;
    if (!card) return;

    const cardW = card.clientWidth || naturalWidth;

    if (cardW < narrowPortrait) {
      // Portrait narrow → scroll (no scaling)
      setScrollMode(true);
      setScale(1);
      setWrapH(naturalHeight);
      return;
    }

    setScrollMode(false);

    // Tablet/phone → scale to fit; desktop → natural size
    const compact = cardW < desktopBreakpoint;
    const s = compact ? Math.min(1, cardW / naturalWidth) : 1;

    setScale(s);
    setWrapH(Math.round(naturalHeight * s));
  }, [desktopBreakpoint, narrowPortrait, naturalHeight, naturalWidth]);

  useLayoutEffect(() => {
    recalc();
    const ro = new ResizeObserver(() => recalc());
    ro.observe(document.body);
    if (cardRef.current) ro.observe(cardRef.current);
    return () => ro.disconnect();
  }, [recalc]);

  // When entering scroll mode, make sure we start from the left (show C first)
  useEffect(() => {
    if (scrollMode && cardRef.current) {
      cardRef.current.scrollLeft = 0;
    }
  }, [scrollMode]);

  return (
    <div
      ref={cardRef}
      className={cardClassName}
      style={{
        minHeight: wrapH ? wrapH + 16 : undefined,
        overflowX: scrollMode ? "auto" : "hidden",
        WebkitOverflowScrolling: scrollMode ? "touch" : "auto",
      }}
    >
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div
          style={{
            width: naturalWidth,
            minWidth: scrollMode ? naturalWidth : undefined,
            transform: scrollMode ? "none" : `scale(${scale})`,
            transformOrigin: "center top",
            willChange: "transform",
            backfaceVisibility: "hidden",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
